import { defineComponent as _defineComponent } from 'vue';
import { vModelText as _vModelText, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withDirectives as _withDirectives, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = ["disabled", "placeholder"];
const _hoisted_2 = { class: "flex items-center justify-between" };
const _hoisted_3 = ["disabled"];
const _hoisted_4 = ["disabled"];
const _hoisted_5 = {
    key: 0,
    class: "grid grid-cols-3"
};
const _hoisted_6 = ["onClick"];
const _hoisted_7 = {
    key: 1,
    class: "grid grid-cols-4 gap-4"
};
const _hoisted_8 = ["disabled", "onClick"];
const _hoisted_9 = {
    key: 2,
    class: "rounded-4 mt-2 flex items-center justify-center bg-gray-100 py-8"
};
const _hoisted_10 = { class: "text-caption-2 text-gray-800" };
import clsx from 'clsx';
import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue';
import { SpriteIcons } from '@/user/components/common';
export default /*@__PURE__*/ _defineComponent({
    __name: 'MonthPicker',
    props: {
        modelValue: { default: '' },
        placeholder: { default: '월을 선택하세요' },
        label: { default: '' },
        className: { default: '' },
        disabled: { type: Boolean, default: false },
        minDate: { default: '' },
        maxDate: { default: '' },
        defaultToCurrent: { type: Boolean, default: false },
        showToday: { type: Boolean, default: false },
        yearRange: { default: 2 },
        minYear: { default: '1900' },
        maxYear: { default: '2999' }
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // string props를 number로 변환
        const minYearNum = computed(() => parseInt(props.minYear || '1900'));
        const maxYearNum = computed(() => parseInt(props.maxYear || '2999'));
        // 상태 관리
        const isOpen = ref(false);
        const isYearMode = ref(false); // 년도 선택 모드
        const dropdownRef = ref();
        const inputRef = ref();
        const currentYear = ref(new Date().getFullYear());
        const inputValue = ref('');
        // input 값과 modelValue 동기화
        watchEffect(() => {
            inputValue.value = props.modelValue || '';
        });
        // 오늘 날짜 표시 형식: yyyy-MM-dd (요일)
        const todayDisplay = computed(() => {
            const today = new Date();
            const year = today.getFullYear();
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const day = today.getDate().toString().padStart(2, '0');
            const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
            const weekday = weekdays[today.getDay()];
            return `오늘 ${year}-${month}-${day} (${weekday})`;
        });
        // 월 목록 생성
        const months = computed(() => {
            const monthNames = [
                '1월',
                '2월',
                '3월',
                '4월',
                '5월',
                '6월',
                '7월',
                '8월',
                '9월',
                '10월',
                '11월',
                '12월',
            ];
            return monthNames.map((name, index) => {
                const monthValue = `${currentYear.value}-${(index + 1).toString().padStart(2, '0')}`;
                const isDisabled = isMonthDisabled(monthValue);
                return {
                    value: monthValue,
                    label: name,
                    disabled: isDisabled,
                };
            });
        });
        // 년도 목록 생성 (최대 9개)
        const years = computed(() => {
            const totalRange = maxYearNum.value - minYearNum.value + 1;
            let startYear, endYear;
            if (totalRange <= 9) {
                // 전체 범위가 9개 이하이면 모든 년도 표시
                startYear = minYearNum.value;
                endYear = maxYearNum.value;
            }
            else {
                // 전체 범위가 9개 초과이면 현재 년도 중심으로 9개 표시
                const currentYearValue = currentYear.value;
                startYear = Math.max(currentYearValue - 4, minYearNum.value);
                endYear = Math.min(currentYearValue + 4, maxYearNum.value);
                // 시작이나 끝에 치우쳐 있으면 9개를 맞추기 위해 조정
                if (endYear - startYear + 1 < 9) {
                    if (startYear === minYearNum.value) {
                        endYear = Math.min(startYear + 8, maxYearNum.value);
                    }
                    else if (endYear === maxYearNum.value) {
                        startYear = Math.max(endYear - 8, minYearNum.value);
                    }
                }
            }
            const yearList = [];
            for (let year = startYear; year <= endYear; year++) {
                yearList.push({
                    value: year,
                    label: `${year}`,
                });
            }
            return yearList;
        });
        // 년도 범위 타이틀 (예: 2020-2026)
        const yearRangeTitle = computed(() => {
            const totalRange = maxYearNum.value - minYearNum.value + 1;
            let startYear, endYear;
            if (totalRange <= 9) {
                // 전체 범위가 9개 이하이면 전체 범위 표시
                startYear = minYearNum.value;
                endYear = maxYearNum.value;
            }
            else {
                // 전체 범위가 9개 초과이면 현재 년도 중심으로 9개 범위 표시
                const currentYearValue = currentYear.value;
                startYear = Math.max(currentYearValue - 4, minYearNum.value);
                endYear = Math.min(currentYearValue + 4, maxYearNum.value);
                // 시작이나 끝에 치우쳐 있으면 9개를 맞추기 위해 조정
                if (endYear - startYear + 1 < 9) {
                    if (startYear === minYearNum.value) {
                        endYear = Math.min(startYear + 8, maxYearNum.value);
                    }
                    else if (endYear === maxYearNum.value) {
                        startYear = Math.max(endYear - 8, minYearNum.value);
                    }
                }
            }
            return `${startYear}-${endYear}`;
        });
        // 화살표 비활성화 상태 확인
        const isPrevDisabled = computed(() => {
            if (isYearMode.value) {
                // 년도 선택 모드에서 이전 화살표 비활성화 조건
                const totalRange = maxYearNum.value - minYearNum.value + 1;
                if (totalRange <= 9) {
                    // 전체 범위가 9개 이하면 화살표 비활성화
                    return true;
                }
                // 현재 표시 범위의 시작이 minYear에 도달했으면 비활성화
                const currentYearValue = currentYear.value;
                const startYear = Math.max(currentYearValue - 4, minYearNum.value);
                return startYear <= minYearNum.value;
            }
            else {
                // 월 선택 모드에서는 currentYear가 minYear에 도달했으면 비활성화
                return currentYear.value <= minYearNum.value;
            }
        });
        const isNextDisabled = computed(() => {
            if (isYearMode.value) {
                // 년도 선택 모드에서 다음 화살표 비활성화 조건
                const totalRange = maxYearNum.value - minYearNum.value + 1;
                if (totalRange <= 9) {
                    // 전체 범위가 9개 이하면 화살표 비활성화
                    return true;
                }
                // 현재 표시 범위의 끝이 maxYear에 도달했으면 비활성화
                const currentYearValue = currentYear.value;
                const endYear = Math.min(currentYearValue + 4, maxYearNum.value);
                return endYear >= maxYearNum.value;
            }
            else {
                // 월 선택 모드에서는 currentYear가 maxYear에 도달했으면 비활성화
                return currentYear.value >= maxYearNum.value;
            }
        });
        // 월이 비활성화되어야 하는지 확인
        const isMonthDisabled = (monthValue) => {
            if (props.minDate && monthValue < props.minDate)
                return true;
            if (props.maxDate && monthValue > props.maxDate)
                return true;
            return false;
        };
        // 년도 변경
        const changeYear = (direction) => {
            if (isYearMode.value) {
                // 년도 선택 모드에서는 9년씩 이동
                if (direction === 'prev') {
                    currentYear.value = Math.max(currentYear.value - 9, minYearNum.value + 4);
                }
                else {
                    currentYear.value = Math.min(currentYear.value + 9, maxYearNum.value - 4);
                }
            }
            else {
                // 월 선택 모드에서는 1년 단위로 이동
                if (direction === 'prev') {
                    currentYear.value = Math.max(currentYear.value - 1, minYearNum.value);
                }
                else {
                    currentYear.value = Math.min(currentYear.value + 1, maxYearNum.value);
                }
            }
        };
        // 년도 헤더 클릭 - 년도 선택 모드로 전환
        const toggleYearMode = () => {
            isYearMode.value = !isYearMode.value;
        };
        // 년도 선택
        const selectYear = (year) => {
            currentYear.value = year;
            isYearMode.value = false; // 월 선택 모드로 돌아감
        };
        // 월 선택
        const selectMonth = (monthValue) => {
            if (isMonthDisabled(monthValue))
                return;
            emit('update:modelValue', monthValue);
            isOpen.value = false;
        };
        // 드롭다운 토글
        const toggleDropdown = () => {
            if (props.disabled)
                return;
            isOpen.value = !isOpen.value;
        };
        // input 관련 이벤트 핸들러
        const handleInputChange = (event) => {
            const target = event.target;
            inputValue.value = target.value;
        };
        const handleInputBlur = () => {
            validateAndUpdateInput();
        };
        const handleInputKeydown = (event) => {
            if (event.key === 'Enter') {
                validateAndUpdateInput();
                isOpen.value = false;
            }
            else if (event.key === 'Escape') {
                inputValue.value = props.modelValue || '';
                isOpen.value = false;
            }
        };
        // input 값 검증 및 업데이트
        const validateAndUpdateInput = () => {
            const value = inputValue.value.trim();
            // YYYY-MM 형식 검증
            const regex = /^\d{4}-\d{2}$/;
            if (!regex.test(value)) {
                // 유효하지 않으면 원래 값으로 복원
                inputValue.value = props.modelValue || '';
                return;
            }
            const [year, month] = value.split('-');
            if (!year || !month) {
                inputValue.value = props.modelValue || '';
                return;
            }
            const yearNum = parseInt(year);
            const monthNum = parseInt(month);
            // 월 범위 검증 (1-12)
            if (monthNum < 1 || monthNum > 12) {
                inputValue.value = props.modelValue || '';
                return;
            }
            // 년도 범위 검증 (minYearNum - maxYearNum)
            if (yearNum < minYearNum.value || yearNum > maxYearNum.value) {
                inputValue.value = props.modelValue || '';
                return;
            }
            // 유효하면 값 업데이트
            emit('update:modelValue', value);
            currentYear.value = yearNum;
        };
        // 외부 클릭 감지
        const handleClickOutside = (event) => {
            if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
                isOpen.value = false;
            }
        };
        // 키보드 이벤트 처리
        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                isOpen.value = false;
            }
        };
        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('keydown', handleKeydown);
            // defaultToCurrent가 true이고 modelValue가 없으면 현재 월로 설정
            if (props.defaultToCurrent && !props.modelValue) {
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
                emit('update:modelValue', currentMonth);
            }
            // modelValue가 있으면 해당 년도로 설정
            if (props.modelValue) {
                const [year] = props.modelValue.split('-');
                if (year) {
                    currentYear.value = parseInt(year);
                }
            }
        });
        onUnmounted(() => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('keydown', handleKeydown);
        });
        // 스타일 클래스
        const buttonClasses = computed(() => clsx('w-full border-1 border-gray-250 rounded-8 text-body-4 px-16 py-11 focus:border-gray-700 focus:text-gray-900 focus:outline-none', {
            'bg-gray-100 cursor-not-allowed': props.disabled,
            'hover:text-blue-700': !props.disabled,
            'border-gray-700': isOpen.value && !props.disabled,
        }, props.className));
        const dropdownClasses = computed(() => clsx('absolute top-full left-0 right-0 p-8 mt-8 bg-white border-1 border-gray-250 rounded-8 shadow-(--shadow-dropdown) z-50 w-209', {
            block: isOpen.value,
            hidden: !isOpen.value,
        }));
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "dropdownRef",
                ref: dropdownRef,
                class: "group relative w-full"
            }, [
                _withDirectives(_createElementVNode("input", {
                    ref_key: "inputRef",
                    ref: inputRef,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((inputValue).value = $event)),
                    class: _normalizeClass(buttonClasses.value),
                    disabled: __props.disabled,
                    placeholder: __props.placeholder,
                    type: "text",
                    onBlur: handleInputBlur,
                    onClick: toggleDropdown,
                    onInput: handleInputChange,
                    onKeydown: handleInputKeydown
                }, null, 42, _hoisted_1), [
                    [_vModelText, inputValue.value]
                ]),
                _createElementVNode("button", {
                    class: "absolute top-1/2 right-12 -translate-y-1/2",
                    type: "button",
                    onClick: toggleDropdown
                }, [
                    _createVNode(_unref(SpriteIcons), {
                        class: _normalizeClass(["size-20",
                            props.disabled
                                ? 'text-gray-400'
                                : 'text-gray-600 group-focus-within:text-gray-900 group-hover:text-blue-700 group-hover:group-focus-within:text-gray-900'
                        ]),
                        name: "calendar"
                    }, null, 8, ["class"])
                ]),
                _createElementVNode("div", {
                    class: _normalizeClass(dropdownClasses.value)
                }, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("button", {
                            class: _normalizeClass([
                                'rounded-4 flex size-36 items-center justify-center',
                                isPrevDisabled.value
                                    ? 'cursor-not-allowed text-gray-400'
                                    : 'text-gray-900 hover:bg-gray-500/10',
                            ]),
                            disabled: isPrevDisabled.value,
                            type: "button",
                            onClick: _cache[1] || (_cache[1] = ($event) => (changeYear('prev')))
                        }, [
                            _createVNode(_unref(SpriteIcons), {
                                class: "size-16",
                                name: "arrow-left"
                            })
                        ], 10, _hoisted_3),
                        _createElementVNode("button", {
                            class: "text-body-5",
                            type: "button",
                            onClick: toggleYearMode
                        }, _toDisplayString(isYearMode.value ? yearRangeTitle.value : currentYear.value), 1),
                        _createElementVNode("button", {
                            class: _normalizeClass([
                                'rounded-4 flex size-36 items-center justify-center',
                                isNextDisabled.value
                                    ? 'cursor-not-allowed text-gray-400'
                                    : 'text-gray-900 hover:bg-gray-500/10',
                            ]),
                            disabled: isNextDisabled.value,
                            type: "button",
                            onClick: _cache[2] || (_cache[2] = ($event) => (changeYear('next')))
                        }, [
                            _createVNode(_unref(SpriteIcons), {
                                class: "size-16",
                                name: "arrow-right"
                            })
                        ], 10, _hoisted_4)
                    ]),
                    (isYearMode.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(years.value, (year) => {
                                return (_openBlock(), _createElementBlock("button", {
                                    key: year.value,
                                    class: "flex h-48 w-64 items-center justify-center",
                                    type: "button",
                                    onClick: _withModifiers(($event) => (selectYear(year.value)), ["stop"])
                                }, [
                                    _createElementVNode("div", {
                                        class: _normalizeClass(["rounded-6 text-body-5 flex h-44 w-60 items-center justify-center", {
                                                'bg-blue-700 text-white': currentYear.value === year.value,
                                                'text-gray-900 hover:bg-gray-500/10 hover:text-gray-900': currentYear.value !== year.value,
                                            }])
                                    }, _toDisplayString(year.label), 3)
                                ], 8, _hoisted_6));
                            }), 128))
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(months.value, (month) => {
                                return (_openBlock(), _createElementBlock("button", {
                                    key: month.value,
                                    class: "flex size-48 items-center justify-center",
                                    disabled: month.disabled,
                                    type: "button",
                                    onClick: ($event) => (selectMonth(month.value))
                                }, [
                                    _createElementVNode("div", {
                                        class: _normalizeClass(["rounded-6 text-body-5 flex size-44 items-center justify-center", {
                                                'bg-blue-700 text-white': __props.modelValue === month.value,
                                                'text-gray-900 hover:bg-gray-500/10 hover:text-gray-900': __props.modelValue !== month.value && !month.disabled,
                                                'cursor-not-allowed text-gray-300': month.disabled,
                                            }])
                                    }, _toDisplayString(month.label), 3)
                                ], 8, _hoisted_8));
                            }), 128))
                        ])),
                    (__props.showToday)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createElementVNode("p", _hoisted_10, _toDisplayString(todayDisplay.value), 1)
                        ]))
                        : _createCommentVNode("", true)
                ], 2)
            ], 512));
        };
    }
});
